import React, { Fragment, useMemo } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getExpandedRowModel,
  ColumnDef,
  flexRender,
  Row,
  getPaginationRowModel,
  ColumnFiltersState,
  getFilteredRowModel,
} from '@tanstack/react-table';
import {
  TableHeader,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableHead,
} from '@/components/ui/table.tsx';
import { DeviceMetricsTablePagination } from './device-metrics-table-pagination.tsx';
import { DeviceMetricsTableToolbar } from './device-metrics-table-toolbar.tsx';
import { cn } from '@/utils/utils.ts';
import {
  DeviceTypeCodes,
  DeviceTypeNames,
} from '@/features/devices/schemas/devices.schemas.ts';
import { LoadingSpinner } from '@/components/ui/loading-spinner.tsx';
import { tableColumns } from './device-metrics-table.columns.tsx';

type TDevice = {
  unit: string;
  fieldCode: number;
  deviceTypeCode: DeviceTypeCodes;
  aggregation: string;
  time: string;
  value: number;
  last24Hours: TDevice[];
};

type TableProps<TData> = {
  data: TData[];
  columns: ColumnDef<TData>[];
  RenderSubComponent: (props: {
    row: Row<TData>;
    plantId: string;
    deviceType: DeviceTypeNames;
    deviceSN?: string;
  }) => React.ReactElement;
  getRowCanExpand: (row: Row<TData>) => boolean;
  plantId: string;
  deviceType: DeviceTypeNames;
  deviceSN: string | undefined;
};

export const DeviceMetricsTable = ({
  data,
  columns,
  RenderSubComponent,
  getRowCanExpand,
  plantId,
  deviceType,
  deviceSN,
  isLoading,
}: TableProps<TDevice> & { isLoading: boolean | undefined }) => {
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);

  // eslint-disable-next-line react-hooks/incompatible-library
  const table = useReactTable<TDevice>({
    data,
    columns,
    initialState: {
      pagination: {
        pageSize: 10,
      },
    },
    state: {
      columnFilters,
    },
    getRowCanExpand,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),

    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const headers = useMemo(() => {
    return tableColumns(deviceType).map((c) => c.label);
  }, [deviceType]);

  return (
    <div className="flex h-full flex-col justify-between p-0 sm:p-2">
      <div className="min-h-[350px]">
        <div className="h-4" />
        <DeviceMetricsTableToolbar table={table} />
        <div className="h-4" />
        <Table>
          <TableHeader className="hidden sm:[display:table-header-group]">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead
                      key={header.id}
                      colSpan={header.colSpan}
                      style={{
                        width: header.getSize() !== 150 ? header.getSize() : undefined,
                        textWrap: 'nowrap',
                      }}
                    >
                      {header.isPlaceholder ? null : (
                        <div>
                          {flexRender(header.column.columnDef.header, header.getContext())}
                        </div>
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => {
                return (
                  <Fragment key={row.id}>
                    <TableRow className="flex h-[380px] flex-col py-2 text-base sm:[display:table-row] sm:h-min sm:py-0 sm:text-sm">
                      {row.getVisibleCells().map((cell, i) => {
                        return (
                          <TableCell
                            key={cell.id}
                            data-cell={headers[i] ? `${headers[i]}` : ''}
                            className={cn(
                              'before:text-muted-foreground block before:block before:text-sm before:font-semibold before:content-[attr(data-cell)] sm:[display:table-cell] sm:before:content-[attr(data-no-cell)]',
                              i === 0 && 'order-4'
                            )}
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                    {row.getIsExpanded() && (
                      <TableRow className="hover:bg-white">
                        <TableCell colSpan={row.getVisibleCells().length}>
                          <RenderSubComponent
                            row={row}
                            plantId={plantId}
                            deviceType={deviceType}
                            deviceSN={deviceSN}
                          />
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 p-0 text-center">
                  {isLoading ? (
                    <div className="flex w-full items-center justify-center py-20">
                      <LoadingSpinner size={50} />
                    </div>
                  ) : (
                    <div className="flex h-full min-h-[240px] w-full items-center justify-center rounded-md bg-gray-50 text-base">
                      <span className="text-muted-foreground">No parameters found</span>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <div>
        <div className="h-4" />
        <DeviceMetricsTablePagination table={table} />
      </div>
    </div>
  );
};
